import merge from 'lodash/merge';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { THEMES } from '../constants';
import { lightShadows, darkShadows } from './shadows';

const baseOptions = {
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box'
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%'
        },
        body: {
          height: '100%'
        },
        '#root': {
          height: '100%'
        },
        '#nprogress .bar': {
          zIndex: '2000 !important'
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  },
  typography: {
    button: {
      fontWeight: 600
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem'
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem'
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem'
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem'
    },
    overline: {
      fontWeight: 600
    }
  }
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e'
            }
          }
        }
      }
    },
    palette: {
      action: {
        active: '#6b778c'
      },
      background: {
        default: '#f4f5f7',
        paper: '#ffffff'
      },
      error: {
        contrastText: '#ffffff',
        //main: '#f44336'
        main: '#FE3265'
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: '#6ac6fd'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6ac6fd'
      },
      success: {
        contrastText: '#ffffff',
        // main: '#4caf50'
        main: '#6ac6fd'
      },
      text: {
        primary: '#172b4d',
        secondary: '#6b778c'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#32d74b'
      },
      theme_bg: {
        contrastText: '#172b4d',
        main: '#f4f5f7',
      },
    },
    shadows: lightShadows
  },
  [THEMES.BLUE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      background: {
        default: '#171c24',
        paper: '#222b36'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        //main: '#f44336'
        main: '#FE3265'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      success: {
        contrastText: '#ffffff',
        //main: '#4caf50'
        main: '#6ac6fd'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#555555'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#32d74b'
      },
      theme_bg: {
        contrastText: '#ffffff',
        main: '#171c24',
      },
    },
    shadows: darkShadows
  },
  [THEMES.GREY]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      background: {
        default: '#1e1f22',
        paper: '#2b2d30'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        //main: '#f44336'
        main: '#FE3265'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      success: {
        contrastText: '#ffffff',
        //main: '#4caf50'
        main: '#6ac6fd'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#555555'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#32d74b'
      },
      theme_bg: {
        contrastText: '#ffffff',
        main: '#383a3c',
      },
    },
    shadows: darkShadows
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      background: {
        default: '#1c1d1f',
        paper: '#24262a'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        //main: '#f44336'
        main: '#FE3265'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      success: {
        contrastText: '#ffffff',
        //main: '#4caf50'
        main: '#6ac6fd'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#555555'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#32d74b'
      },
      theme_bg: {
        contrastText: '#ffffff',
        main: '#383a3c',
      },
    },
    shadows: darkShadows
  },
  [THEMES.METAL]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      background: {
        default: '#1e2227',
        paper: '#23272e'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        //main: '#f44336'
        main: '#FE3265'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6AC6FD'
      },
      success: {
        contrastText: '#ffffff',
        //main: '#4caf50'
        main: '#6ac6fd'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#555555'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#32d74b'
      },
      theme_bg: {
        contrastText: '#ffffff',
        main: '#171c24',
      },
    },
    shadows: darkShadows
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      background: {
        default: '#1c2531',
        paper: '#293142'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#01ab56'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#01ab56'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      },
      grey: {
        contrastText: '#ffffff',
        main: '#555555'
      },
      grey_light: {
        contrastText: '#ffffff',
        main: '#aaaaaa'
      },
      teal: {
        contrastText: '#ffffff',
        main: '#52edc7'
      },
      violet: {
        contrastText: '#ffffff',
        main: '#c644fc'
      },
      green: {
        contrastText: '#ffffff',
        main: '#42bf3f'
      },
      theme_bg: {
        contrastText: '#ffffff',
        main: '#1c2531',
      },
    },
    shadows: darkShadows
  },

};

// Severity colors — consistent across all themes
// Keep in sync with blusensor-severity.ts SEVERITY_COLORS in blusensor-cloud-aws
const severityPalette = {
  severity_info: {
    contrastText: '#ffffff',
    main: '#999999',
  },
  severity_normal: {
    contrastText: '#ffffff',
    main: '#6ac6fd',
  },
  severity_deviation: {
    contrastText: '#ffffff',
    main: '#ffcc00',
  },
  severity_warning: {
    contrastText: '#ffffff',
    main: '#FFA600',
  },
  severity_critical: {
    contrastText: '#ffffff',
    main: '#fe2967',
  },
};

export const createCustomTheme = (config = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(merge({}, baseOptions, themeOptions, {
    ...(config.roundedCorners && {
      shape: {
        borderRadius: 16
      }
    }),
    palette: severityPalette
  }, {
    direction: config.direction
  }));

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
